import cookies from 'js-cookie';

import { cookieNames } from '@monorepo/common';

// Shared UTM attribution helpers for the react/jobs SPA (app.cpgjobs.com).
// MIRRORS the cpgjobs Next.js `app/lib/utm.ts` cookie contract EXACTLY — same
// cookie (`cpg_utm`), same tracking keys, same fixed-order canonical serialization
// (`key=encodeURIComponent(value)` joined by `&`) — so a UTM captured on
// www.cpgjobs.com and one captured on app.cpgjobs.com are byte-identical and the
// backend's HttpUtility.ParseQueryString reporting path reads either unchanged.
//
// The `.cpgjobs.com` cookie is readable/writable on BOTH www. (cpgjobs) and app.
// (this SPA), so UTMs ride across the funnel with no cross-domain URL forwarding —
// the same cross-subdomain model the SSO `bevnet_auth_token` cookie already uses.
//
// Cross-app cookie interop: cpgjobs wraps the canonical string in
// encodeURIComponent on write / decodeURIComponent on read. js-cookie's default
// converters (used here) round-trip losslessly against that — js-cookie's read
// fully decodeURIComponent's the stored value and its write encodeURIComponent's
// it (only un-escaping cookie-safe chars), so either app reads what the other
// wrote. (Confirmed in the cpgjobs `app/lib/utm.ts` contract note.)

// Fixed capture + serialization order. Existing UTM dimensions and click IDs keep
// their established order; the potentially long landing-page URL is last so any
// downstream truncation affects it before the existing attribution fields.
export const TRACKING_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'fbclid',
  'msclkid',
  'utm_page',
] as const;

const COOKIE_EXPIRES_DAYS = 90;

// Prod: scope to `.cpgjobs.com` so www. and app. share the cookie. Localhost (and
// any non-cpgjobs host): omit Domain — host-only, exactly like the SSO dev shim.
// Cookies are not port-scoped, so the cpgjobs dev port and this SPA's dev port
// still share the host-only `localhost` cookie.
const cookieDomain = (): string | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }
  const host = window.location.hostname.toLowerCase();
  return host === 'cpgjobs.com' || host.endsWith('.cpgjobs.com') ? '.cpgjobs.com' : undefined;
};

/**
 * Filter a URL's params to the allow-listed tracking keys and re-serialize them in
 * the fixed TRACKING_KEYS order as a canonical query string
 * (e.g. `utm_source=google&utm_medium=cpc&gclid=abc`). Returns '' when the URL
 * carries no non-page tracking keys — callers treat '' as "nothing to capture".
 * When at least one non-page tracking key is present, an explicit `utm_page` is
 * preserved; otherwise `landingPageUrl` is normalized to origin + pathname and
 * used as a fallback.
 */
export const extractUtm = (params: URLSearchParams, landingPageUrl?: string): string => {
  // Build the canonical string identically to cpgjobs' extractUtm: each present
  // key as `key=encodeURIComponent(value)`, joined by `&`, in TRACKING_KEYS order.
  // (URLSearchParams.toString() would form-encode spaces as `+` — a serialization
  // mismatch with cpgjobs — so we assemble the string by hand instead.)
  const hasNonPageTracking = TRACKING_KEYS.some(
    (key) => key !== 'utm_page' && Boolean(params.get(key))
  );
  let fallbackPage: string | undefined;
  if (!params.get('utm_page') && hasNonPageTracking && landingPageUrl) {
    try {
      const url = new URL(landingPageUrl);
      fallbackPage = `${url.origin}${url.pathname}`;
    } catch {
      fallbackPage = undefined;
    }
  }

  const parts: string[] = [];
  TRACKING_KEYS.forEach((key) => {
    const value = key === 'utm_page'
      ? (hasNonPageTracking ? params.get(key) || fallbackPage : undefined)
      : params.get(key);
    if (value) {
      parts.push(`${key}=${encodeURIComponent(value)}`);
    }
  });
  return parts.join('&');
};

/** Read the shared last-touch UTM cookie (raw canonical query string, or undefined). */
export const readUtmCookie = (): string | undefined => cookies.get(cookieNames.utmParams);

/**
 * Last-touch write: overwrite `cpg_utm` with the given canonical value. Only call
 * with a non-empty value (see extractUtm) so a UTM-less page never clears it.
 */
export const writeUtmCookie = (value: string): void => {
  if (!value || typeof window === 'undefined') {
    return;
  }
  const domain = cookieDomain();
  cookies.set(cookieNames.utmParams, value, {
    expires: COOKIE_EXPIRES_DAYS,
    path: '/',
    sameSite: 'lax',
    // Secure only on https (prod). Localhost is http, where Secure would make the
    // browser drop the cookie — mirrors cpgjobs' `protocol === 'https:'` guard.
    ...(window.location.protocol === 'https:' ? { secure: true } : {}),
    ...(domain ? { domain } : {}),
  });
};
